import React from 'react';
import {
    Card,
    ExamApplicationTabs,
    ResetPasswordModal,
    AppType,
    IResetPasswordChangeData,
    ApplicantDocuments,
} from 'components';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Button } from 'react-bootstrap';
import { ExamApplicationStatus, ExamStatus } from '../../api/ApiClient';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { useLocalErrorHandler } from 'hooks';
import query from '../../helpers/const/query';
import printIcon from 'components/assets/svg/print_icon.svg';
import styles from './ExamApplicationDetailsContainer.module.scss';
import cn from 'classnames';

const ExamApplicationDetailsContainer = ({
    applicantId,
    onFetchExamApplicationDetails,
    setErrorHandler,
    onPush,
    onAdmitToExam,
    examApplicationDetails,
    setApplicantTempPassword,
    apiError,
    withoutCard,
    onCloseClick,
}: Props) => {
    useLocalErrorHandler(setErrorHandler);

    const [processing, setProcessing] = React.useState(false);
    const [showPasswordModal, setShowPasswordModal] = React.useState(false);
    const [disableSubmitButton, setDisableSubmitButton] = React.useState(true);

    React.useEffect(() => {
        const fetch = async () => {
            setProcessing(true);
            await onFetchExamApplicationDetails(applicantId);
            setProcessing(false);
        };

        fetch();
    }, [applicantId]);

    const onGoBack = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const from = urlParams.get('from');

        if (!from) {
            onPush('/exam-timetables');
        } else {
            onPush(`/${from}`);
        }
    };

    const onSubmit = async () => {
        if (!examApplicationDetails) return;

        setProcessing(true);
        await onAdmitToExam(applicantId);
        setProcessing(false);
        onPush(`/exam/${examApplicationDetails.exam.id}?from=${query.FROM.TIMETABLES}`);
    };

    const onChangePassword = async ({ newPassword }: IResetPasswordChangeData) => {
        if (!applicantId) return false;

        const res = await setApplicantTempPassword({ applicantId, body: { newPassword } });

        return !!res;
    };

    const applicant = examApplicationDetails && examApplicationDetails.applicant;
    const isPassingStatus = examApplicationDetails && examApplicationDetails.exam.status === ExamStatus.IsPassing;
    const fullName = applicant ? `${applicant.lastName} ${applicant.firstName} ${applicant.middleName}` : '';
    const showAgreementLink = Boolean(examApplicationDetails && examApplicationDetails.documents
        && (examApplicationDetails.status === ExamApplicationStatus.Paid
            || examApplicationDetails.status === ExamApplicationStatus.AdmittedToExam
            || examApplicationDetails.status === ExamApplicationStatus.IsPassing));

    const examTabChild = React.useCallback(() => (examApplicationDetails && examApplicationDetails.status === ExamApplicationStatus.AdmittedToExam ? (
        <>
            <Button
              className="mr-5"
              variant="outline-secondary"
              onClick={() => setShowPasswordModal(true)}
            >
                Назначить временный пароль
            </Button>
            <ResetPasswordModal
              title="Установка временного пароля"
              show={showPasswordModal}
              onHide={() => setShowPasswordModal(false)}
              apiError={apiError}
              onChangePassword={onChangePassword}
              withoutOldPass
              withoutRepeatPass
            >
                <span className="d-block mb-4 font-weight-normal">
                    Назначьте новый временный пароль для соискателя. Пароль должен состоять, как минимум, из шести символов.
                    Соискатель сможет сменить этот пароль по завершении экзамена.
                </span>
            </ResetPasswordModal>
        </>
    ) : null), [examApplicationDetails, showPasswordModal, apiError]);

    const notAllowed = Boolean(examApplicationDetails
        && examApplicationDetails.documents
        && examApplicationDetails.status === ExamApplicationStatus.Paid
        && examApplicationDetails.exam.status !== ExamStatus.IsApplying);

    const renderCloseModalButton = () => (withoutCard && onCloseClick ? (
        <Button variant="outline-secondary" className={styles.close_button} onClick={onCloseClick}>
            {notAllowed ? 'Отмена' : 'Закрыть'}
        </Button>
    ) : null);

    const renderModalFooter = () => (examApplicationDetails ? (
        <div className={cn(styles.footer_buttons_block, withoutCard && styles.footer_buttons_block_modal)}>
            {notAllowed && (
                <>
                    <Button
                      variant="primary"
                      className="mr-5"
                      onClick={onSubmit}
                      disabled={disableSubmitButton}
                      data-test-admit-to-exam
                    >
                        Допустить к экзамену
                    </Button>
                </>
            )}

            {showAgreementLink && !withoutCard && (
                <Button
                  className={styles.print_button}
                  variant="outline-primary"
                  as="a"
                  target="_blank"
                  href={examApplicationDetails.agreementUrl}
                >
                    <img src={printIcon} alt="print-icon" className="mr-1" /> Распечатать пакет документов
                </Button>
            )}

            {examTabChild()}

            {renderCloseModalButton()}
        </div>
    ) : null);

    const onChangeSwitch = (active: boolean[]) => {
        setDisableSubmitButton(!active.every(e => e === true));
    };

    return (
        <>
            {withoutCard && examApplicationDetails ? (
                <>
                    <ApplicantDocuments
                      documents={examApplicationDetails.documents}
                      onChangeSwitch={notAllowed ? onChangeSwitch : undefined}
                      withoutPadding
                    />
                    {renderModalFooter()}
                </>
            ) : (
                    <Card
                      withoutPadding
                      withBackButton
                      onClickBack={onGoBack}
                      title={isPassingStatus ? fullName : 'Допуск соискателя'}
                      category={isPassingStatus ? 'Идет сдача' : fullName}
                      categoryClassName={(isPassingStatus && styles.passingCategory) || ''}
                      apiError={apiError}
                      showLoader={processing}
                    >
                        {examApplicationDetails ? (
                            <ExamApplicationTabs
                              examApplicationDetails={examApplicationDetails}
                              examTabChild={examTabChild || undefined}
                              appType={AppType.Expert}
                            //   tab={tab}
                            />
                        ) : null}
                    </Card>
                )}
        </>
    );
};

const mapState = (state: IRootState) => ({
    examApplicationDetails: state.examApplications.examApplicationDetails,
    apiError: state.errors.error,
});

const mapDispatch = (dispatch: Dispatch) => ({
    onFetchExamApplicationDetails: dispatch.examApplications.fetchExamApplicationDetails,
    onAdmitToExam: dispatch.examApplications.admitToExam,
    setErrorHandler: dispatch.errors.setErrorHandler,
    setApplicantTempPassword: dispatch.expert.setApplicantTempPasswordAsync,
    onPush: (key: string) => dispatch(push(key)),
});

export default connect(mapState, mapDispatch)(ExamApplicationDetailsContainer);

interface ExamApplicationDetailsOwnProps {
    applicantId: string;
    withoutCard?: boolean;
    onCloseClick?: () => void;
    tab?: string;
}

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>
type Props = connectedProps & ExamApplicationDetailsOwnProps;
